import { useEffect, useState } from 'react'
import { DocumentStatus } from '@/components/StatusTag'
import { useApiFeatureFlag } from '@/hooks/useFeatureFlag'
import { apiService } from '@/service/api'
import type { DocumentRecord } from '@/types/catalog'
import { getDocumentStatus } from '@/utils/documentStatus'

export const EXPLAIN_VIDEO_FLAG = 'explain_video_enabled'

/** Rows a brief cannot be made from: derived artifacts and anchors with no file. */
const NOT_A_SOURCE = new Set(['graph', 'video', 'live_stream'])

/**
 * A local `just up` serves the app from the Vite dev server, where PostHog is
 * disabled by design (posthog-config.ts), so a PostHog flag can never turn on
 * there. The feature is on in that one mode; every deployed build is a
 * production bundle and the flag alone decides. Vitest runs in mode `test`.
 */
export const LOCAL_DEV_MODE = import.meta.env.MODE === 'development'

/**
 * The server's verdict, fetched once per page load and shared by every row
 * menu and board rail that asks. A browser that blocks the analytics domain
 * never loads PostHog flags and would otherwise sit on the registry default,
 * off, while the API would happily accept the request.
 */
let serverVerdict: Promise<boolean> | null = null

function fetchServerVerdict(): Promise<boolean> {
  if (!serverVerdict) {
    // Resolved lazily so a synchronous failure (an API stub without this
    // method, as every catalog test has) lands in the catch, never in render.
    serverVerdict = Promise.resolve()
      .then(() => apiService.getVideoBriefEnabled())
      .then((verdict) => verdict === true)
      .catch(() => {
        serverVerdict = null // let a later mount retry after a transient failure
        return false
      })
  }
  return serverVerdict
}

/** Test seam: forget a cached verdict between cases. */
export function resetExplainAsVideoVerdict(): void {
  serverVerdict = null
}

/**
 * Whether "Explain as video" is offered to this person: the PostHog flag as
 * the browser sees it, or the same flag as the server sees it, or the local
 * dev server. The API applies the server rule on every request.
 */
export function useExplainAsVideoEnabled(): boolean {
  const { enabled: browserFlag } = useApiFeatureFlag(EXPLAIN_VIDEO_FLAG, false)
  const [serverFlag, setServerFlag] = useState(false)

  useEffect(() => {
    let alive = true
    void fetchServerVerdict().then((verdict) => {
      if (alive) setServerFlag(verdict)
    })
    return () => {
      alive = false
    }
  }, [])

  return browserFlag || serverFlag || LOCAL_DEV_MODE
}

/**
 * Whether "Explain as video" applies to this catalog row: a processed file of
 * any real type. "Processed" is the catalog's own rule (`getDocumentStatus`),
 * which reads `is_indexed` and a parsed drawing's graph as well as `status`,
 * since many rows carry no `status` at all. A row still processing has
 * nothing a brief could be cut from yet.
 */
export function canExplainAsVideo(doc: DocumentRecord): boolean {
  if (NOT_A_SOURCE.has(doc.type)) return false
  return getDocumentStatus(doc) === DocumentStatus.success
}
